import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';

import {EntityService} from '../../abstract/EntityService';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService extends EntityService {

  protected readonly url = 'iam-invoice';

  getForms(): Observable<any> {
    return this.http.get(`${this.url}/template/forms`);
  }

  getPdfForSave(params: any): Observable<any> {
    const pdfParams: any = {};
    for (const key of Object.keys(params)) {
      if (params[key] !== null && params[key] !== undefined) {
        pdfParams[key] = params[key];
      }
    }
    return this.http.get(`${this.url}/invoices/make-pdf`, {params: pdfParams});
  }

  uploadFile(data: any): Observable<any> {
    return this.http.post(`${this.url}/invoices/make-pdf`, data);
  }

  getFileHistory(lazyLoadEvent: string, invoiceId?: any): Observable<any> {
    return this.http.get(`${this.url}/invoices/${invoiceId}/agr-file-history?lazyLoadEvent=${lazyLoadEvent}`);
  }

  getSerials(enumGroupEnumId: string, invoiceForm?: string): Observable<any> {
    let params: any = {enumGroupEnumId};
    if (invoiceForm) {
      params = {invoiceForm};
    }
    return this.http.get(`${this.url}/template/serials-by-form`, {params});
  }

  getAmountToWords(uomId: string, amount: number): Observable<any> {
    return this.http.get(`iam-common/amount-to-words`, {params: {uomId, amount}});
  }

  getDiscountTypes(): Observable<any> {
    return this.http.get(`iam-common/invoice/discount-type`);
  }

  getPaymentInstrumentTypes(): Observable<any> {
    return this.http.get(`iam-common/invoice/payment-instrument-type`);
  }

  getBusinessTypes(): Observable<any> {
    return this.http.get(`iam-common/business-types`);
  }

  getBusinessLocations(): Observable<any> {
    return this.http.get(`${this.url}/sellers/self/business-locations`);
  }

  updateBusinessLocations(item: any): Observable<any> {
    return this.http.put(`${this.url}/sellers/self/business-locations`, item);
  }

  getBusinessEligibility(): Observable<any> {
    return this.http.get(`${this.url}/sellers/self/business-eligibility`);
  }

  getCurrencies(): Observable<any> {
    return this.http.get(`iam-common/currencies`);
  }

  getAdjustmentTypes(): Observable<any> {
    return this.http.get(`iam-common/invoice/adjustment-types`);
  }

  getAdjustmentItemTypes(): Observable<any> {
    return this.http.get(`iam-common/invoice/adjustment-item-types`);
  }

  getSpecialProduct(): Observable<any> {
    return this.http.get(`iam-common/specialized-product-info`);
  }

  getRelatedFormTypes(): Observable<any> {
    return this.http.get(`iam-common/invoice/related-form-type`);
  }

  getTaxExemptPercent(): Observable<any> {
    return this.http.get(`iam-common/invoice/tax-exempt-percent`);
  }

  getItemClassTypes(): Observable<any> {
    return this.http.get(`iam-common/invoice/item-class-type`);
  }

  getTaxConfig(invoiceTemplatePartyId: string): Observable<any> {
    return this.http.get(`${this.url}/template/${invoiceTemplatePartyId}/tax-config`);
  }

  getExtraFields(invoiceTemplatePartyId: string): Observable<any> {
    return this.http.get(`${this.url}/invoices/extra-items-fields?invoiceTemplatePartyId=${invoiceTemplatePartyId}`);
  }

  override getList(lazyLoadEvent: string): Observable<any> {
    return this.http.get(`${this.url}/invoices/list?lazyLoadEvent=${lazyLoadEvent}`);
  }

  fetchByIds(params: any): Observable<any> {
    return this.http.get(`${this.url}/invoices/fetch-by-ids`, {params});
  }

  search(params: any): Observable<any> {
    return this.http.post(`${this.url}/invoices/search`, params);
  }

  get(id: string): Observable<any> {
    return this.http.get(`${this.url}/invoices/${id}/detail`);
  }
  relatedTemplate(id: string): Observable<any> {
    return this.http.get(`${this.url}/related-template?invoiceId=${id}`);
  }

  preview(item: any): Observable<any> {
    return this.http.post(`${this.url}/invoices/preview`, item, {responseType: 'blob'});
  }

  sendDraftEmail(id: string): Observable<any> {
    return this.http.put(`${this.url}/invoices/${id}/send-draft-email`, {});
  }

  override create(item: any): Observable<any> {
    return this.http.post(`${this.url}/invoices/create`, item);
  }

  override update(id: string, item: any): Observable<any> {
    return this.http.put(`${this.url}/invoices/${id}/update`, item);
  }

  deleteBatch(invoiceIds: string): Observable<any> {
    return this.http.delete(`${this.url}/invoices/delete-batch`, {params: {invoiceIds}});
  }

  clone(id: string): Observable<any> {
    return this.http.post(`${this.url}/invoices/${id}/clone`, {});
  }

  importExcel(data: any): Observable<any> {
    return this.http.post(`${this.url}/invoices/import-excel`, data);
  }

  importShipmentExcel(data: any): Observable<any> {
    return this.http.post(`${this.url}/internal-shipping/import-excel`, data);
  }

  rangesDownload(params: any): Observable<any> {
    return this.http.get(`${this.url}/invoices/ranges-download`, {params});
  }

  changePaymentStatus(id: string, item: any): Observable<any> {
    return this.http.put(`${this.url}/invoices/${id}/change-payment-status`, item);
  }

  override delete(id: string): Observable<any> {
    return this.http.delete(`${this.url}/invoices/${id}/delete`);
  }

  getTVanLog(id: string): Observable<any> {
    return this.http.get(`${this.url}/invoices/${id}/tvan-log`);
  }

  sendEmail(invoiceId: string, data: any): Observable<any> {
    return this.http.put(`${this.url}/invoices/${invoiceId}/send-email`, data);
  }

  batchDownload(invoiceIds: string, toAddresses: string): Observable<any> {
    return this.http.get(`${this.url}/invoices/batch-download`, {params: {invoiceIds, toAddresses}});
  }

  getConverted(invoiceId: string): Observable<any> {
    return this.http.get(`${this.url}/invoices/${invoiceId}/converted`);
  }

  setConverted(invoiceId: string, data: any): Observable<any> {
    return this.http.put(`${this.url}/invoices/${invoiceId}/converted`, data);
  }

  putToWorkEffort(invoiceIds: Array<string>): Observable<any> {
    return this.http.post(`${this.url}/invoices/work-effort`, {invoiceIds});
  }

  checkFields(data: any): Observable<any> {
    return this.http.put(`${this.url}/invoices/check-warning`, data);
  }

  uploadAgrContent(invoiceId: string, data: any): Observable<any> {
    return this.http.post(`${this.url}/invoices/${invoiceId}/upload-agr-file`, data);
  }

}
